// Overview dashboard (SM-785, plan D66). Six panels from ONE call,
// GET /api/broker/{id}/overview?currency=, composed server-side from the
// same reads the detail screens use, so a figure here always equals the
// one on the screen it links to. Each panel renders its own state (OK /
// EMPTY / UNAVAILABLE with the error code / FORBIDDEN) and a stale mark;
// own and descendant figures are two labelled columns, never merged, and a
// leaf shows no descendants column at all. Beside them, a third "Total"
// column shows the server's own + descendants (SM-897) for earnings,
// revenue, funding (not Returnable: "—") and activity; the page adds
// nothing itself. Exposure has no total: its subtree figure already
// includes the node.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope, useShellSearch } from "@/components/layout/ScopeContext";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Money } from "@/components/ui/Money";
import { Time, TableFrame } from "@/components/ui/Common";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { fetchOverview, keys } from "@/lib/queries";
import { hasCapability, navItem } from "@/lib/capabilities";
import {
  ACTIVITY_FIGURES,
  EARNINGS_ROWS,
  PANEL_DETAIL,
  activityFigure,
  attentionItems,
  attentionTarget,
  overviewPanels,
  showDescendants,
  showTotal,
  type ActivityFigure,
  type PanelKey,
  type PanelView,
} from "@/lib/overview";
import { formatBps } from "@/lib/units";
import { describeError } from "@/i18n/errorCopy";
import { useI18n } from "@/i18n/I18nProvider";
import type {
  BrokerOverviewResponse,
  OverviewActivityFigures,
  OverviewEarningsFigures,
  OverviewFundingFigures,
  OverviewRevenueFigures,
} from "@/lib/types";

export const Route = createFileRoute("/_app/")({
  beforeLoad: capabilityGuard("VIEW"),
  component: OverviewPage,
});

function OverviewPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { nodeId, currency } = useScope();
  const probe = { queryKey: keys.overview(nodeId, currency), queryFn: () => fetchOverview(nodeId, currency) };
  const q = useQuery({ ...probe, refetchInterval: 60_000, enabled: guard.kind === "allow" });

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("overview.title")} subtitle={t("overview.subtitle")} coverage="split" money />
      {q.isLoading ? (
        <LoadingState lines={6} />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.data ? (
        <Dashboard data={q.data} />
      ) : null}
    </Guarded>
  );
}

function Dashboard({ data }: { data: BrokerOverviewResponse }) {
  const { t, formatNumber } = useI18n();
  const views = overviewPanels(data);
  return (
    <div className="space-y-4">
      <p className="text-2xs text-t-3">
        {data.has_descendants
          ? t("overview.descendantCount", { count: formatNumber(data.descendant_count) })
          : t("overview.leaf")}{" "}
        · {t("overview.generatedAt")} <Time ms={data.generated_at_unix_ms} />
      </p>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel panel="earnings" view={views.earnings}>
          <EarningsBody data={data} />
        </Panel>
        <Panel panel="revenue" view={views.revenue}>
          <RevenueBody data={data} />
        </Panel>
        <Panel panel="funding" view={views.funding}>
          <FundingBody data={data} />
        </Panel>
        <Panel panel="exposure" view={views.exposure}>
          <ExposureBody data={data} />
        </Panel>
        <Panel panel="activity" view={views.activity}>
          <ActivityBody data={data} />
        </Panel>
        <Panel panel="attention" view={views.attention}>
          <AttentionBody data={data} />
        </Panel>
      </div>
    </div>
  );
}

function Panel({ panel, view, children }: { panel: PanelKey; view: PanelView; children: ReactNode }) {
  const { t, locale } = useI18n();
  const search = useShellSearch();
  const { capabilities } = useScope();
  // Link to the detail screen only when the role can open it: a panel the
  // role may not see links nowhere rather than to a refusal.
  const detail = PANEL_DETAIL[panel];
  const link = detail && view.kind !== "forbidden" && hasCapability(capabilities, navItem(detail).requires) ? detail : null;
  const titleId = `panel-${panel}`;
  return (
    <Card className="flex flex-col gap-3" aria-labelledby={titleId} role="region">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={titleId} className="section-title">
          {t(`overview.panel.${panel}`)}
        </h2>
        <div className="flex items-center gap-2">
          {(view.kind === "ok" || view.kind === "empty") && view.stale && <Badge tone="warn">{t("overview.stale")}</Badge>}
          {link && (
            <Link to={navItem(link).to} search={search as never} className="text-2xs font-semibold text-neon hover:underline">
              {t("overview.openDetail", { screen: t(`nav.${link}`) })}
            </Link>
          )}
        </div>
      </div>
      {view.kind === "ok" && children}
      {view.kind === "empty" && <p className="text-sm text-t-3">{t(`overview.empty.${panel}`)}</p>}
      {view.kind === "forbidden" && (
        <p className="rounded-md border border-dashed border-line-2 p-3 text-sm text-t-3" role="status">
          {t("overview.forbidden")}
        </p>
      )}
      {view.kind === "unavailable" && (
        <div className="banner warn mb-0" role="status">
          <span aria-hidden>⚠</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-t-1">{t("overview.unavailable")}</p>
            <p className="text-xs text-t-2">{describeError({ code: view.errorCode }, locale).title}</p>
            <p className="mono text-2xs text-t-3">{view.errorCode}</p>
          </div>
        </div>
      )}
      {view.kind === "unknown" && (
        <p className="text-sm text-t-3" role="status">
          {t("overview.unknownState", { state: view.state || "—" })}
        </p>
      )}
      {(view.kind === "ok" || view.kind === "empty" || view.kind === "unavailable") && view.asOf && (
        <p className="text-2xs text-t-3">
          {t("overview.asOf")} <Time ms={view.asOf} />
        </p>
      )}
    </Card>
  );
}

/**
 * Figure rows with a "This node" column and, when present, a separate
 * "Descendants" column and the server's "Total" column after it.
 */
function SplitTable({
  label,
  rows,
  withDescendants,
  withTotal = false,
}: {
  label: string;
  rows: { key: string; label: string; own: ReactNode; desc?: ReactNode; total?: ReactNode }[];
  withDescendants: boolean;
  withTotal?: boolean;
}) {
  const { t } = useI18n();
  return (
    <TableFrame label={label}>
      <table className="tbl">
        <thead>
          <tr>
            <th scope="col">{t("overview.figure")}</th>
            <th scope="col" className="text-right">
              {t("overview.own")}
            </th>
            {withDescendants && (
              <th scope="col" className="text-right">
                {t("overview.descendants")}
              </th>
            )}
            {withTotal && (
              <th scope="col" className="text-right">
                {t("overview.total")}
                <span className="block text-2xs font-normal normal-case tracking-normal text-t-3">{t("overview.totalSub")}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row" className="font-medium text-t-2">
                {r.label}
              </th>
              <td className="text-right">{r.own}</td>
              {withDescendants && <td className="text-right">{r.desc ?? "—"}</td>}
              {withTotal && <td className="text-right">{r.total ?? <span className="text-t-3">—</span>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </TableFrame>
  );
}

function EarningsBody({ data }: { data: BrokerOverviewResponse }) {
  const { t } = useI18n();
  const p = data.earnings;
  const withDesc = showDescendants(data, p.descendants);
  const withTotal = showTotal(data, p);
  const c = data.currency;
  // fee totals only (SM-861): the per-role split is on Revenue
  const label: Record<keyof OverviewEarningsFigures, string> = {
    all_time: t("overview.earnings.allTime"),
    today: t("overview.earnings.today"),
    period: t("overview.earnings.period"),
    previous: t("overview.earnings.previous"),
  };
  const rows = EARNINGS_ROWS.map((k) => ({
    key: k,
    label: label[k],
    own: <Money currency={c} value={p.own?.[k]} />,
    desc: withDesc ? <Money currency={c} value={p.descendants?.[k]} /> : undefined,
    total: withTotal ? <Money currency={c} value={p.total?.[k]} /> : undefined,
  }));
  return (
    <>
      {p.period_from_unix_ms ? (
        <p className="text-2xs text-t-3">
          {t("overview.earnings.window")} <Time ms={p.period_from_unix_ms} /> → <Time ms={p.period_to_unix_ms} />
        </p>
      ) : null}
      <SplitTable label={t("overview.panel.earnings")} rows={rows} withDescendants={withDesc} withTotal={withTotal} />
    </>
  );
}

function RevenueBody({ data }: { data: BrokerOverviewResponse }) {
  const { t } = useI18n();
  const p = data.revenue;
  const c = data.currency;
  const withDesc = showDescendants(data, p.descendants);
  const withTotal = showTotal(data, p);
  const figs: { key: keyof OverviewRevenueFigures; label: string }[] = [
    { key: "pending", label: t("revenue.state.pending") },
    { key: "held", label: t("revenue.state.held") },
    { key: "available", label: t("revenue.state.available") },
    { key: "payout_reserved", label: t("revenue.state.payoutReserved") },
  ];
  return (
    <>
      <SplitTable
        label={t("overview.panel.revenue")}
        withDescendants={withDesc}
        withTotal={withTotal}
        rows={figs.map((f) => ({
          key: f.key,
          label: f.label,
          own: <Money currency={c} value={p.own?.[f.key]} />,
          desc: withDesc ? <Money currency={c} value={p.descendants?.[f.key]} /> : undefined,
          total: withTotal ? <Money currency={c} value={p.total?.[f.key]} /> : undefined,
        }))}
      />
    </>
  );
}

function FundingBody({ data }: { data: BrokerOverviewResponse }) {
  const { t } = useI18n();
  const p = data.funding;
  const c = data.currency;
  const withDesc = showDescendants(data, p.descendants);
  const withTotal = showTotal(data, p);
  const figs: { key: keyof OverviewFundingFigures; label: string }[] = [
    { key: "pool_available", label: t("funding.poolAvailable") },
    { key: "pool_reserved", label: t("funding.poolReserved") },
    { key: "allocated_to_users", label: t("funding.allocatedToUsers") },
    { key: "returnable", label: t("funding.returnable") },
  ];
  return (
    <SplitTable
      label={t("overview.panel.funding")}
      withDescendants={withDesc}
      withTotal={withTotal}
      rows={figs.map((f) => ({
        key: f.key,
        label: f.label,
        own: <Money currency={c} value={p.own?.[f.key]} />,
        desc: withDesc ? <Money currency={c} value={p.descendants?.[f.key]} /> : undefined,
        // Returnable has no total: each descendant returns to its own
        // parent, so the cell is "—", never 0 (SM-897)
        total: withTotal && f.key !== "returnable" ? <Money currency={c} value={p.total?.[f.key]} /> : undefined,
      }))}
    />
  );
}

function ExposureBody({ data }: { data: BrokerOverviewResponse }) {
  const { t } = useI18n();
  const p = data.exposure;
  const c = data.currency;
  return (
    <div className="space-y-2 text-sm">
      {(p.blocked || p.warning) && (
        <div className={p.blocked ? "banner danger mb-0" : "banner warn mb-0"} role="status">
          <span aria-hidden>⚠</span>
          <span>{p.blocked ? t("risk.blocked") : t("risk.warning")}</span>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-2">
        <Fig label={t("risk.ownExposure")} value={<Money currency={c} value={p.own_exposure} />} />
        <Fig
          label={t("risk.subtreeExposure")}
          sub={t("risk.subtreeExposureSub")}
          value={<Money currency={c} value={p.subtree_exposure} />}
        />
        {p.has_limit ? (
          <>
            <Fig label={t("risk.limit")} value={<Money currency={c} value={p.limit} />} />
            <Fig label={t("risk.headroom")} value={<Money currency={c} value={p.headroom} />} />
            <Fig
              label={t("risk.utilisation")}
              value={t("risk.utilisationOf", { used: formatBps(p.utilisation_bps ?? 0), warn: formatBps(p.warning_threshold_bps ?? 0) })}
            />
          </>
        ) : (
          <Fig label={t("risk.limit")} value={t("risk.noOwnLimit")} />
        )}
      </dl>
    </div>
  );
}

function ActivityBody({ data }: { data: BrokerOverviewResponse }) {
  const { t, formatNumber } = useI18n();
  const p = data.activity;
  const c = data.currency;
  const withDesc = showDescendants(data, p.descendants);
  const withTotal = showTotal(data, p);
  const labels: Record<ActivityFigure, string> = {
    active_accounts: t("overview.activity.activeAccounts"),
    open_orders: t("overview.activity.openOrders"),
    open_positions: t("overview.activity.openPositions"),
    fills: t("overview.activity.fills"),
    traded_value: t("overview.activity.tradedValue"),
  };
  // A figure a source could not read is "unavailable" — its wire 0 is
  // never drawn as a real 0 (unavailable_figures, plan D66).
  const cell = (figures: OverviewActivityFigures | null | undefined, key: ActivityFigure) => {
    const f = activityFigure(p, figures, key);
    if (f.kind === "unavailable")
      return (
        <span className="text-2xs text-t-3" title={f.errorCode}>
          {t("overview.activity.figureUnavailable")}
        </span>
      );
    if (f.kind === "absent") return "—";
    return key === "traded_value" ? <Money currency={c} value={String(f.value)} /> : <span className="num">{formatNumber(Number(f.value))}</span>;
  };
  return (
    <>
      {p.error_code && (
        <p className="text-2xs text-t-3" role="status">
          {t("overview.activity.partial", { code: p.error_code })}
        </p>
      )}
      <SplitTable
        label={t("overview.panel.activity")}
        withDescendants={withDesc}
        withTotal={withTotal}
        rows={ACTIVITY_FIGURES.map((key) => ({
          key,
          label: labels[key],
          own: cell(p.own, key),
          desc: withDesc ? cell(p.descendants, key) : undefined,
          total: withTotal ? cell(p.total, key) : undefined,
        }))}
      />
    </>
  );
}

function AttentionBody({ data }: { data: BrokerOverviewResponse }) {
  const { t, formatNumber } = useI18n();
  const search = useShellSearch();
  const { capabilities } = useScope();
  const items = attentionItems(data);
  if (items.length === 0) return <p className="text-sm text-t-3">{t("overview.attention.none")}</p>;
  return (
    <ul className="divide-y divide-line-1">
      {items.map((it) => {
        const owner = attentionTarget(it.kind);
        const target = owner && hasCapability(capabilities, navItem(owner).requires) ? owner : null;
        const label = t(`attention.${it.kind}`, { count: formatNumber(it.count) });
        const node = it.broker_id || search.node;
        return (
          <li key={`${it.kind}-${it.broker_id ?? ""}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span className="text-sm text-t-1">{label}</span>
            {target ? (
              <Link
                to={navItem(target).to}
                search={{ ...search, node, ...(it.kind === "PAUSED_ACCOUNTS" ? { status: "SUSPENDED" } : {}) } as never}
                className="text-2xs font-semibold text-neon hover:underline"
              >
                {t("overview.openDetail", { screen: t(`nav.${target}`) })}
              </Link>
            ) : (
              <span className="mono text-2xs text-t-3">{it.kind}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Fig({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <div className="rounded-md border border-line-1 p-2">
      <dt className="text-2xs font-semibold uppercase tracking-wide text-t-3">{label}</dt>
      <dd className="mt-0.5 text-t-1">{value}</dd>
      {sub && <dd className="mt-0.5 text-2xs text-t-3">{sub}</dd>}
    </div>
  );
}
